"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Search as SearchIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { ViewMenu } from "@/components/editor/view-menu"
import { AutoActionsMenu } from "@/components/editor/auto-actions-panel"
import { HomeToolbar } from "@/components/editor/ribbon/home-toolbar"
import { InsertToolbar } from "@/components/editor/ribbon/insert-toolbar"
import { EquationEditorPopup } from "@/components/editor/ribbon/equation-editor-popup"
import { DrawToolbar } from "@/components/editor/ribbon/draw-toolbar"
import { DesignToolbar } from "@/components/editor/ribbon/design-toolbar"
import { LayoutToolbar } from "@/components/editor/ribbon/layout-toolbar"
import { MailingsToolbar } from "@/components/editor/ribbon/mailing-toolbar"
import { useRibbonActions } from "@/components/editor/ribbon/use-ribbon-actions"
import { useRibbonShortcuts } from "@/components/editor/ribbon/use-ribbon-shortcuts"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const tabs = [
  { name: "Home", icon: "/homeIcon.svg" },
  { name: "Insert", icon: "/insertIcon.svg" },
  // { name: "Draw", icon: "/drawIcon.svg" },
  { name: "Design", icon: "/designIcon.svg" },
  { name: "Layout", icon: "/layoutIcon.svg" },
  // { name: "Reference", icon: "/refrenceIcon.svg" },
  // { name: "Mailings", icon: "/mailingIcon.svg" },
  // { name: "Review", icon: "/reviewIcon.svg" },
  // { name: "Help", icon: "/helpIcon.svg" },
] as const

type Tab = (typeof tabs)[number]["name"]

const TOOLBAR_TABS = new Set<Tab>([
  "Home",
  "Insert",
  // "Draw",
  "Design",
  "Layout",
  // "Mailings",
])

const CLOSE_DELAY_MS = 180

function ChromeIconButton({
  src,
  alt,
  onClick,
}: {
  src: string
  alt: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      aria-label={alt}
      // Keep Monaco focused so undo/redo operate on the editor stack
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "flex h-[22px] w-[28px] items-center justify-center",
        "hover:bg-[var(--ed-ribbon-icon-hover)]",
        "transition-colors",
      )}
    >
      <img src={src} alt="" draggable={false} className="h-3.5 w-3.5" />
    </button>
  )
}

function ActiveToolbar({
  tab,
  onOpenEquation,
}: {
  tab: Tab
  onOpenEquation?: () => void
}) {
  switch (tab) {
    case "Home":
      return <HomeToolbar />
    case "Insert":
      return <InsertToolbar onOpenEquation={onOpenEquation} />
    // case "Draw":
    //   return <DrawToolbar />
    case "Design":
      return <DesignToolbar />
    case "Layout":
      return <LayoutToolbar />
    // case "Mailings":
    //   return <MailingsToolbar />
    default:
      return null
  }
}

export function RibbonMenu() {
  const [hoveredTab, setHoveredTab] = useState<Tab | null>(null)
  const [popupLeft, setPopupLeft] = useState(8)
  const [searchQuery, setSearchQuery] = useState("")
  const [equationOpen, setEquationOpen] = useState(false)
  const [equationDraft, setEquationDraft] = useState<string | undefined>()
  const actions = useRibbonActions()
  useRibbonShortcuts()

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({})
  const barRef = useRef<HTMLDivElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)

  const runSearch = useCallback(
    (direction: "next" | "prev" = "next") => {
      actions.findInDocument(searchQuery, direction)
    },
    [actions, searchQuery],
  )

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const openTab = useCallback(
    (tab: Tab) => {
      clearCloseTimer()
      if (!TOOLBAR_TABS.has(tab)) {
        setHoveredTab(null)
        return
      }
      const el = tabRefs.current[tab]
      const bar = barRef.current
      if (el && bar) {
        const tabRect = el.getBoundingClientRect()
        const barRect = bar.getBoundingClientRect()
        setPopupLeft(Math.max(8, tabRect.left - barRect.left))
      }
      setHoveredTab(tab)
    },
    [clearCloseTimer],
  )

  const scheduleClose = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      setHoveredTab(null)
    }, CLOSE_DELAY_MS)
  }, [clearCloseTimer])

  // Keep popup within the bar width after it mounts / changes
  useEffect(() => {
    if (!hoveredTab || !popupRef.current || !barRef.current) return
    const popup = popupRef.current
    const bar = barRef.current
    const pad = 8
    const maxLeft = Math.max(pad, bar.clientWidth - popup.offsetWidth - pad)
    setPopupLeft((prev) => Math.min(Math.max(pad, prev), maxLeft))
  }, [hoveredTab])

  useEffect(() => {
    return () => clearCloseTimer()
  }, [clearCloseTimer])

  const showPopup = hoveredTab !== null && TOOLBAR_TABS.has(hoveredTab)

  return (
    <div ref={barRef} className="relative z-40 shrink-0">
      {/* Single-row tab bar — does not shrink the editor */}
      <div
        className={cn(
          "flex h-9 select-none items-center",
          "bg-[var(--ed-ribbon-bg)]",
          "border-b border-[var(--ed-ribbon-border)]",
          "text-xs text-[var(--ed-ribbon-text)]",
        )}
      >
        <div className="ribbon-scroll flex h-full min-w-0 flex-1 items-stretch overflow-x-auto overflow-y-hidden">
          {tabs.map((tab, index) => {
            const isOpen = hoveredTab === tab.name
            const hasMenu = TOOLBAR_TABS.has(tab.name)
            return (
              <div
                key={tab.name}
                className="flex h-full items-stretch"
                onMouseEnter={() => openTab(tab.name)}
                onMouseLeave={scheduleClose}
              >
                {index === 0 && (
                  <div
                    className="my-1.5 w-px shrink-0 self-stretch bg-[var(--ed-ribbon-divider)]"
                    aria-hidden
                  />
                )}
                <button
                  ref={(node) => {
                    tabRefs.current[tab.name] = node
                  }}
                  type="button"
                  aria-expanded={hasMenu ? isOpen : undefined}
                  aria-haspopup={hasMenu ? "true" : undefined}
                  onClick={() => {
                    if (!hasMenu) return
                    if (isOpen) {
                      setHoveredTab(null)
                    } else {
                      openTab(tab.name)
                    }
                  }}
                  className={cn(
                    "box-border flex h-full items-center gap-1.5 whitespace-nowrap px-3",
                    "font-normal transition-colors duration-100",
                    isOpen
                      ? "bg-[var(--ed-ribbon-tab-active-bg)] text-[var(--ed-ribbon-tab-active-text)]"
                      : "hover:bg-[var(--ed-ribbon-tab-hover)]",
                  )}
                >
                  <img
                    src={tab.icon}
                    alt=""
                    draggable={false}
                    style={{ filter: "var(--ed-icon-filter-mono, none)" }}
                    className={cn(
                      "h-3.5 w-3.5",
                      isOpen ? "opacity-100" : "opacity-80",
                    )}
                  />
                  {tab.name}
                </button>
                <div
                  className="my-1.5 w-px shrink-0 self-stretch bg-[var(--ed-ribbon-divider)]"
                  aria-hidden
                />
              </div>
            )
          })}
        </div>

        <div className="ml-auto flex h-full shrink-0 items-center gap-2 pr-3 pl-4">
          <div className="relative w-[148px]">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  runSearch(e.shiftKey ? "prev" : "next")
                } else if (e.key === "Escape") {
                  e.preventDefault()
                  setSearchQuery("")
                }
              }}
              placeholder="Search"
              aria-label="Search in document"
              className={cn(
                "h-[22px] rounded-[4px] border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)] pl-7 pr-2 text-xs shadow-none",
                "placeholder:text-[#9CA3AF]",
                "focus-visible:ring-1 focus-visible:ring-[#5B8CFF]",
              )}
            />
            <SearchIcon className="pointer-events-none absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2 text-[#6B7280]" />
          </div>

          <div
            className={cn(
              "flex h-[22px] shrink-0 items-center overflow-hidden rounded-[4px]",
              "border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)]",
            )}
          >
            <ChromeIconButton src="/undo.svg" alt="Undo" onClick={actions.undo} />
            <div className="h-3.5 w-px bg-[var(--ed-ribbon-field-border)]" aria-hidden />
            <ChromeIconButton src="/redo.svg" alt="Redo" onClick={actions.redo} />
          </div>

          <div className="ml-1 flex items-center gap-2">
            <ViewMenu />
            <div
              className="h-4 w-px shrink-0 bg-[var(--ed-ribbon-field-border)]"
              aria-hidden
            />
            <AutoActionsMenu />
          </div>
        </div>
      </div>

      {/* Floating toolbar popup — overlays editor, no layout shift */}
      {showPopup && hoveredTab && (
        <div
          ref={popupRef}
          className="pointer-events-auto absolute top-full z-50 pt-1"
          style={{ left: popupLeft }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          <ActiveToolbar
            tab={hoveredTab}
            onOpenEquation={() => {
              setEquationDraft(actions.getEquationDraft() ?? undefined)
              setEquationOpen(true)
              setHoveredTab(null)
            }}
          />
        </div>
      )}

      {equationOpen && (
        <EquationEditorPopup
          initialLatex={equationDraft}
          onClose={() => setEquationOpen(false)}
          onInsert={(body) => {
            actions.equation(body)
            setEquationOpen(false)
          }}
        />
      )}
    </div>
  )
}

/** Re-export tooltip helper for font hover hints if needed elsewhere */
export function RibbonHoverHint({
  src,
  children,
}: {
  src: string
  children: React.ReactNode
}) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={6}
        hideArrow
        className="border-0 bg-transparent p-0 shadow-none"
      >
        <img src={src} alt="" draggable={false} className="max-w-none drop-shadow-lg" />
      </TooltipContent>
    </Tooltip>
  )
}
